import { motion } from 'framer-motion';
import {
  UserPlus,
  MessageSquare,
  Users,
  BookOpen,
  DollarSign,
  Building2,
  ClipboardCheck,
  Award,
  BarChart3,
  Send,
  Bell,
  Globe,
  CalendarDays,
  NotebookPen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function CollegeFeaturesPage() {
  const features = [
    {
      icon: UserPlus,
      title: 'Student Admissions & Profile Management',
      description:
        'Streamlined admission process with digital forms, document upload, and automated enrollment. Track student history from admission to alumni status.',
      color: 'bg-blue-500',
    },
    {
      icon: BookOpen,
      title: 'Program & Curriculum Management',
      description:
        'Design degree programs, manage subjects across semesters, and track course completion. Flexible structures for undergraduate and postgraduate levels.',
      color: 'bg-yellow-500',
    },
    {
      icon: Users,
      title: 'Department & Batch Management',
      description:
        'Create and manage departments, batches, and student assignments. Smart allocation based on program capacity and requirements.',
      color: 'bg-green-500',
    },
    {
      icon: DollarSign,
      title: 'Fee Collection & Financial Management',
      description:
        'Complete fee lifecycle management — invoicing, collection, reminders, and receipts. Support for multiple payment modes and installments.',
      color: 'bg-emerald-500',
    },
    {
      icon: MessageSquare,
      title: 'Lead & Enquiry Management System',
      description:
        'Capture and track all prospective student enquiries. Automated follow-ups and conversion tracking to improve admissions.',
      color: 'bg-purple-500',
    },
    {
      icon: ClipboardCheck,
      title: 'Examination & Assessment Management',
      description:
        'Create semester exam schedules, manage question banks, and conduct assessments. Automated grading and result publishing.',
      color: 'bg-red-500',
    },
    {
      icon: CalendarDays,
      title: 'Holiday Management',
      description:
        'Plan and publish college holidays, semester breaks, and special days in one place. Keep faculty, students, and parents aligned with a clear academic calendar.',
      color: 'bg-rose-500',
    },
    {
      icon: NotebookPen,
      title: 'Homework Management',
      description:
        'Assign coursework and homework by department or batch, set due dates, and track submissions. Faculty and students stay updated on daily learning tasks.',
      color: 'bg-violet-500',
    },
    {
      icon: Award,
      title: 'Certificate & Document Management',
      description:
        'Generate and manage degree certificates, transcripts, and academic documents. Digital certificates with verification system.',
      color: 'bg-indigo-500',
    },
    {
      icon: Building2,
      title: 'Hostel & Campus Management',
      description:
        'Room allocation, hostel fee tracking, and campus facility management. Streamlined operations for residential colleges.',
      color: 'bg-orange-500',
    },
    {
      icon: BarChart3,
      title: 'Reports, Insights & Analytics Dashboard',
      description:
        'Comprehensive reports and dashboards for data-driven decisions. Track attendance, performance, fees, and placements in real-time.',
      color: 'bg-pink-500',
    },
    {
      icon: Send,
      title: 'WhatsApp Communication Gateway',
      description:
        'Send automated notifications, fee reminders, and announcements directly to students and parents via WhatsApp.',
      color: 'bg-green-600',
    },
    {
      icon: Bell,
      title: 'Notice & Announcement Center',
      description:
        'Broadcast important updates to students, faculty, and staff instantly. Schedule announcements and track engagement.',
      color: 'bg-blue-600',
    },
    {
      icon: Globe,
      title: 'Website & Digital Presence Management',
      description:
        'Professional website for your college with CMS integration. Showcase achievements, placements, and connect with the community.',
      color: 'bg-teal-500',
    },
  ];

  return (
    <div data-testid="college-features-page" className="pt-20">
      <section className="py-24 bg-gradient-to-br from-navy to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-black mb-6 font-heading">
              Powerful Features For Modern Colleges
            </h1>
            <p className="text-base leading-relaxed text-slate-300 max-w-3xl mx-auto">
              Comprehensive suite of tools designed to automate every aspect of
              college management. From admissions to alumni, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      <section
        data-testid="college-features-grid-section"
        className="py-24 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                data-testid={`college-feature-module-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 feature-card"
              >
                <div
                  className={`${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-navy mb-3 font-heading">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="college-whatsapp-showcase-section"
        className="py-24 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-green-100 px-4 py-2 rounded-full mb-6">
                <span className="text-green-700 font-medium text-sm">
                  Most Popular Feature
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
                WhatsApp Powered Communication
              </h2>
              <p className="text-base text-slate-600 mb-6">
                Reach students and parents instantly on their preferred platform.
                Send fee reminders, exam schedules, attendance updates, and
                announcements directly to WhatsApp.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Automated fee reminders and payment confirmations
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Semester exam schedule and result notifications
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Bulk announcements for events and holidays
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="bg-emerald-light/10 p-1 rounded-full mt-1">
                    <Send className="h-4 w-4 text-emerald" />
                  </div>
                  <span className="text-slate-700">
                    Placement and internship opportunity alerts
                  </span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 to-green-600 p-8 rounded-2xl shadow-2xl">
                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-emerald rounded-full flex items-center justify-center">
                      <Send className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy">VibhumEduSys</p>
                      <p className="text-xs text-slate-500">Automated Messages</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-700">
                        Dear Student, semester fee of ₹25,000 is due. Please clear
                        by 15th Jan.
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-700">
                        Your 3rd semester results are now published. Check the portal.
                      </p>
                    </div>
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-700">
                        Campus placement drive on 20th Jan. Register by 18th Jan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="college-cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            See All Features In Action
          </h2>
          <p className="text-base text-slate-600 mb-8">
            Book a personalized demo to explore how VibhumEduSys can transform your
            college operations.
          </p>
          <Link to="/register">
            <Button
              data-testid="college-features-demo-button"
              size="lg"
              className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8 cta-button"
            >
              Book Free Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
